import { ApiError, isForbidden, isNotFound, type timeoff } from '@cwos/api-client';
import type { Severity } from '@cwos/design-tokens';
import { narrowedTo, ORGANIZATION, UNKNOWN_POPULATION, type Population } from '../figureState.js';
import type { ScopeReader } from '../readScope.js';
import { formatLeaveWindow } from '../../lib/timeFormat.js';

/**
 * Pure logic for REVIEWING TIME OFF — the reviewer half of `timeoff`.
 *
 * `POST /timeoff/requests/:id/decision` has been live since migration
 * `1755120000000` and had no caller in any admin surface. Lane L88 bound the
 * client functions and built the WORKER half, so a worker could file a request
 * and withdraw it; nobody could decide one. A filed request sat `pending`
 * forever, and `settingsNav.ts` advertised `backedBy: GET /timeoff/requests`
 * for a screen that never called it.
 *
 * Everything here is DOM-free so the rules can be tested against the SERVER's
 * rules rather than against a rendering of them.
 *
 * WHAT IS DELIBERATELY NOT HERE: any re-derivation of who the caller may decide
 * for. `timeoff:review` is seeded to the stock `supervisor` role
 * ORGANIZATION-WIDE and is a DOOR, not reach — the routes' own header says so
 * at length. Reach is `worker.supervisor_id`, resolved inside the transaction
 * that locks the row, and separation of duties (`assertMayDecide`) is resolved
 * against the same row. Neither is knowable from `/auth/me`'s flattened key
 * list. So the control is offered to a `timeoff:review` holder and the server's
 * refusal is rendered as a refusal when it arrives.
 */

// --- what the caller may do -------------------------------------------------

/**
 * MAY THIS PRINCIPAL SEE THE REGISTER?
 *
 * `GET /timeoff/requests` is `guard(request, reply, 'timeoff:read')`. That key
 * is seeded to the stock `worker` role organization-wide, so it is nearly
 * always true — it gates the query's `enabled:` so an unheld key produces no
 * request and no cached 403.
 */
export const TIMEOFF_READ = 'timeoff:read';

export function mayReadTimeOff(has: (key: string | undefined) => boolean): boolean {
  return has(TIMEOFF_READ);
}

/**
 * MAY THIS PRINCIPAL DECIDE A REQUEST?
 *
 * `timeoff:review`. The door only. See the file header.
 */
export const TIMEOFF_DECIDE = 'timeoff:review';

export function mayDecideTimeOff(has: (key: string | undefined) => boolean): boolean {
  return has(TIMEOFF_DECIDE);
}

/**
 * WHOSE REGISTER THE UNFILTERED READ IS SHOWING.
 *
 * The scope lives in the SQL, so an out-of-scope request is MISSING rather than
 * refused and no count over the rows can reveal that a narrowing happened. A
 * supervisor without `timeoff:read_all` is looking at themselves plus the
 * workers on their `worker.supervisor_id` edge, under a heading that otherwise
 * reads as the whole organization's — which would make an empty list a claim
 * about everybody.
 *
 * `timeoff:read_all` is the ONLY organization-wide key here, exactly as
 * `ORG_WIDE_TIMEOFF` says on the server. `timeoff:review` and `timeoff:manage`
 * are deliberately not in that list: the seeded `supervisor` role holds both
 * organization-wide, so reading either as reach would turn "somebody with a
 * supervisor role" into "this worker's supervisor" and put every colleague's
 * free-text `reason` on the screen.
 *
 * `can` — the org-wide question — and not `has`: a `timeoff:read_all` devolved
 * to a single program is `true` in the flattened key list and `false` to the
 * server's `can()`, and mirroring the flattened form would suppress the scope
 * note over a register the server had narrowed.
 */
export function timeOffPopulation(reader: ScopeReader): Population {
  if (reader.isLoading || reader.isError) return UNKNOWN_POPULATION;
  if (reader.can('timeoff:read_all')) return ORGANIZATION;
  return narrowedTo('you and the workers assigned to you');
}

// --- whose calendar the dates are on ----------------------------------------

/**
 * WHICH CALENDAR A LEAVE WINDOW IS BEING RENDERED ON, and whether that is the
 * organization's.
 *
 * A time-off interval is a pair of INSTANTS. Which local days it covers is a
 * question only a timezone can answer, and the answer differs: an interval that
 * opens at 18:00 in `America/Chicago` is already the next day in London. The
 * organization's calendar is the one the request was made against and the one
 * the schedule and the timesheet week are cut on (`org_local_date()`), so it is
 * the only correct one to decide against.
 *
 * THE SERVER DOES NOT SEND IT. `TimeOffRequestView` carries no zone, and the
 * organization's zone is only readable through `GET /organization`, which is
 * `guard(..., 'org:manage')` — a key a supervisor deciding leave has no reason
 * to hold. `Site.effectiveTimezone` is a SITE's zone and is not the
 * organization's in a multi-site tenant, so it is not a substitute.
 *
 * Rather than silently falling back to the device's calendar — which is the
 * defect, not the fix — the fallback is NAMED. `viewer` means the days on
 * screen were computed in the reader's own zone and may not be the days the
 * organization has on file.
 */
export type ReviewCalendar =
  /** The organization's own IANA zone, read from `GET /organization`. */
  | { kind: 'org'; zone: string }
  /**
   * The reader's device zone, used because the organization's is not available
   * — and `why` says which of the three reasons it is. `withheld` is a
   * statement about the account, `unavailable` about a failed read, and
   * `pending` about a read still in flight. They are three different sentences
   * and an empty one for all three is how "we did not look" gets rendered as a
   * fact.
   */
  | { kind: 'viewer'; zone: string; why: 'withheld' | 'unavailable' | 'pending' };

/** The parts of the organization read this decision needs. */
export interface OrgZoneSource {
  /** `organization.timezone` when the read succeeded, else undefined. */
  timezone: string | undefined;
  /** The reader holds `org:manage`, so the query was allowed to run at all. */
  permitted: boolean;
  isLoading: boolean;
  isError: boolean;
}

/** The device's IANA zone, or `'UTC'` when the platform will not say. */
export function viewerZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * Which calendar to render on, and why.
 *
 * Ordered by what is actually known, most certain first — the same ordering
 * `nameResolver` uses for the same reason. `permitted` is checked before the
 * query state because a query that never ran is not "still loading".
 */
export function reviewCalendar(
  source: OrgZoneSource,
  device: string = viewerZone(),
): ReviewCalendar {
  if (!source.permitted) return { kind: 'viewer', zone: device, why: 'withheld' };
  if (source.isError) return { kind: 'viewer', zone: device, why: 'unavailable' };
  if (source.timezone === undefined || source.timezone.trim() === '') {
    return { kind: 'viewer', zone: device, why: source.isLoading ? 'pending' : 'unavailable' };
  }
  return { kind: 'org', zone: source.timezone };
}

/**
 * The sentence that says whose calendar these dates are on, or null when they
 * are on the organization's and nothing needs saying.
 *
 * It never claims the days are wrong — they may well be identical — only that
 * this screen could not check. Asserting a mismatch it cannot observe would be
 * the same failure in the other direction.
 */
export function calendarNote(calendar: ReviewCalendar): string | null {
  if (calendar.kind === 'org') return null;
  const shown = `These dates are shown on your device\u2019s calendar (${calendar.zone}), not the organization\u2019s.`;
  switch (calendar.why) {
    case 'withheld':
      return `${shown} Reading the organization\u2019s timezone needs the organization-settings permission, which your account does not hold, so a request that crosses midnight there may read as a different day here.`;
    case 'unavailable':
      return `${shown} We couldn\u2019t read the organization\u2019s timezone just now, so a request that crosses midnight there may read as a different day here.`;
    case 'pending':
      return `${shown} We are still reading the organization\u2019s timezone.`;
  }
}

/** One request's window, on whichever calendar {@link reviewCalendar} settled. */
export function describeWindow(request: timeoff.TimeOffRequest, calendar: ReviewCalendar): string {
  return formatLeaveWindow(request.startsAt, request.endsAt, calendar.zone);
}

// --- reading a request ------------------------------------------------------

/** The four statuses, in words, with the tone a pill should carry. */
export const TIME_OFF_STATUS_WORDS: Record<
  timeoff.TimeOffRequestStatus,
  { word: string; tone: Severity }
> = {
  pending: { word: 'Waiting on a decision', tone: 'look' },
  approved: { word: 'Approved', tone: 'good' },
  denied: { word: 'Denied', tone: 'urgent' },
  cancelled: { word: 'Withdrawn', tone: 'plain' },
};

/**
 * A status in a reviewer's words.
 *
 * An unrecognised value gets a generic sentence rather than the raw token: the
 * four are a database CHECK constraint and a fifth would mean the server grew
 * one, not that this screen should print it. `TimeOffRequestView.status` is
 * typed `string` on the server, so this really can be reached.
 */
export function describeStatus(status: string): { word: string; tone: Severity } {
  return (
    TIME_OFF_STATUS_WORDS[status as timeoff.TimeOffRequestStatus] ?? {
      // `unsure`, not `plain`: the palette's own word for "we do not know", and
      // a status this screen cannot name is not a neutral one.
      word: 'Status not recognised',
      tone: 'unsure',
    }
  );
}

// --- deciding ---------------------------------------------------------------

/** The query key family every time-off read lives under. */
export const TIMEOFF_KEY = ['timeoff'] as const;

/** The pending-decision queue's own key. */
export const PENDING_TIMEOFF_KEY = [...TIMEOFF_KEY, 'requests', 'pending'] as const;

/**
 * What has to be refreshed once leave is decided.
 *
 * APPROVING LEAVE IS NOT ONLY A STATUS CHANGE. `approvedLeaveIntervals` and
 * `workerOnApprovedLeaveAt` both read `status = 'approved'`, so the moment a
 * request flips, "is this worker off?" has a different answer everywhere it is
 * asked — and the schedule and board surfaces that will consult it are stale.
 * Refreshing only the queue leaves the rest of the product answering from
 * before the decision.
 */
export const TIMEOFF_DECISION_INVALIDATES: readonly (readonly string[])[] = [
  ['timeoff'],
  ['schedule'],
  ['admin-views'],
];

/** A minimal stand-in for the bit of react-query's client this needs. */
export interface Invalidator {
  invalidateQueries: (filters: { queryKey: readonly unknown[] }) => unknown;
}

/** Refresh everything a decision changed. Unit-tested directly. */
export function invalidateAfterTimeOffDecision(qc: Invalidator): void {
  for (const key of TIMEOFF_DECISION_INVALIDATES) {
    qc.invalidateQueries({ queryKey: key });
  }
}

/**
 * HOW A FAILED DECISION SHOULD BE RENDERED — five outcomes, not two.
 *
 * The decision route can answer five materially different ways, and every one
 * of them asks something different of the reviewer. Collapsing them into
 * "something went wrong, try again" is how a permanent refusal grows a retry
 * button, and collapsing them into "not allowed" is how a 409 gets reported as
 * a permission problem.
 *
 *   403 REFUSED   two causes, both permanent for this caller and this request:
 *                 `guard` says you do not hold `timeoff:review`, or
 *                 `assertMayDecide` says nobody decides a request they are the
 *                 subject of or filed. The server writes a different sentence
 *                 for each. No retry.
 *   404 GONE      the request is outside your reach, or there is no such
 *                 request — and the route answers the SAME 404 for both,
 *                 deliberately, because the existence of a colleague's leave
 *                 request is itself the disclosure. So this must not claim
 *                 either one. No retry.
 *   409 SETTLED   somebody decided or withdrew it while this screen was open.
 *                 `RequestStateError`. The honest response is to re-read the
 *                 row, not to offer the button again.
 *   422 REJECTED  `TimeOffValidationError` — the server refused the content of
 *                 the decision. Retrying the same content cannot work.
 *   otherwise     a 500, a dropped connection, an expired session. Genuinely
 *                 retryable, and the ONLY branch that gets a retry.
 */
export type TimeOffDecisionFailure =
  | { kind: 'refused'; message: string; refusals: readonly string[] }
  | { kind: 'gone'; message: string }
  | { kind: 'settled'; message: string }
  | { kind: 'rejected'; message: string }
  | { kind: 'failed'; message: string };

const REFUSAL_FALLBACK =
  'Your account cannot decide this request. Deciding leave needs reach over the worker it is ' +
  'about, and nobody may decide a request they filed or are the subject of.';

/**
 * THE STRUCTURED REFUSALS, ONE SENTENCE EACH, NEVER JOINED.
 *
 * `ForbiddenError.refusals` is the transport's shape for every
 * authority-bearing door in this product, and `permission_not_held` and
 * `scope_not_held` are DIFFERENT refusals with different fixes: the first is
 * answered by somebody granting you a key, the second by narrowing the grant
 * you are attempting. `ForbiddenError.message` for a ceiling refusal is a
 * run-on join of every refusal's sentence, from which no reader can recover
 * which key failed for which reason — so when the structure is on the body it
 * is rendered as a LIST and the join is discarded.
 *
 * STATE OF THE WORLD, so nobody reads this as a claim it is exercised in
 * production: `timeoff`'s own routes answer a PLAIN 403 today
 * (`{ error, message, requestId }`, no `refusals`), so this returns `[]` for
 * every refusal that domain currently produces, and the caller falls back to
 * the single server sentence. It is here because a decision route is exactly
 * the kind of door that acquires a ceiling refusal, and because the failure it
 * prevents — a scope refusal answered by widening a grant to organization
 * scope — is an ESCALATION, not a cosmetic error.
 */
export function refusalSentences(err: unknown): string[] {
  if (!isForbidden(err)) return [];
  const refusals = err.refusals;
  if (refusals === null || refusals.length === 0) return [];
  return refusals.map((refusal) => {
    const said = refusal.message.trim();
    // The key is named alongside the sentence, because two refusals of
    // DIFFERENT codes about the SAME key, and two of the same code about
    // different keys, are both things a reader has to be able to tell apart.
    const key = refusal.permissionKey;
    return key ? `${said} (${refusal.code}: ${key})` : `${said} (${refusal.code})`;
  });
}

export function timeOffDecisionFailure(err: unknown): TimeOffDecisionFailure {
  if (isForbidden(err)) {
    const structured = refusalSentences(err);
    const said = typeof err.message === 'string' && err.message.trim() ? err.message : '';
    return {
      kind: 'refused',
      // When the body carried structure the joined `message` is DROPPED and
      // replaced by a lead-in: it is a run-on of the same sentences, and
      // printing it above the list would say each reason twice while still
      // being unreadable as a whole.
      message:
        structured.length > 0
          ? 'The server refused this, and it gave more than one reason. Each needs its own fix:'
          : said || REFUSAL_FALLBACK,
      refusals: structured,
    };
  }
  if (isNotFound(err)) {
    return {
      kind: 'gone',
      message:
        'This request is not one you can decide. It is either outside the workers you reach, or ' +
        'it is no longer there — the server answers the same way for both, on purpose, because ' +
        'saying which would itself say whether a colleague has leave on file.',
    };
  }
  if (err instanceof ApiError && err.status === 409) {
    return {
      kind: 'settled',
      message:
        (typeof err.message === 'string' && err.message.trim()
          ? `${err.message} `
          : 'This request is no longer waiting on a decision. ') +
        'Somebody decided or withdrew it while this was open — reload the queue to see where it ' +
        'stands. Nothing you pressed was recorded.',
    };
  }
  if (err instanceof ApiError && (err.status === 422 || err.status === 400)) {
    return {
      kind: 'rejected',
      message:
        (typeof err.message === 'string' && err.message.trim()
          ? err.message
          : 'The server would not accept that decision.') +
        ' The request is unchanged, and sending the same thing again will be refused the same way.',
    };
  }
  return {
    kind: 'failed',
    message:
      'We couldn’t record that decision just now. The request is unchanged — it has not been ' +
      'approved or denied.',
  };
}

/**
 * The body for a decision.
 *
 * `deny`, never `reject`: `decideRequestSchema` is `z.enum(['approve','deny'])`
 * and anything else is a 400 at the boundary. An empty or whitespace-only note
 * is OMITTED rather than sent as `''` — the field is optional and the server
 * trims, so sending an empty string records "the reviewer wrote nothing" as if
 * it were a note.
 */
export function decisionBody(
  decision: 'approve' | 'deny',
  note: string,
): timeoff.TimeOffDecisionInput {
  const trimmed = note.trim();
  return { decision, ...(trimmed ? { decisionNote: trimmed } : {}) };
}

/**
 * WHICH DECISION IS IN FLIGHT — the key a busy label must be read from.
 *
 * One mutation serves both buttons, so `isPending` is true for BOTH the moment
 * either is pressed. This product has already shipped "Recording…" on Approve
 * while the operator pressed Deny, on the timekeeping correction pair and again
 * on the SMS consent pair; both were fixed by reading the mutation's
 * `variables` instead. A queue adds a second axis: several rows are on screen
 * at once, so the running decision must be keyed by REQUEST ID as well, or
 * pressing Deny on one row puts a busy label on every row's Deny button.
 */
export interface RunningDecision {
  requestId: string;
  decision: 'approve' | 'deny';
}

/**
 * The label for one button, given what is actually running.
 *
 * Returns the resting word unless THIS row's THIS decision is the one in
 * flight. `running` is `undefined` when nothing is.
 */
export function decisionButtonLabel(
  requestId: string,
  decision: 'approve' | 'deny',
  running: RunningDecision | undefined,
  resting: string,
): string {
  if (running && running.requestId === requestId && running.decision === decision) {
    return decision === 'approve' ? 'Recording the approval…' : 'Recording the denial…';
  }
  return resting;
}
